import {
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  useQuery,
} from '@apollo/client';
import { DocumentNode } from 'graphql';
import { useCallback } from 'react';

/**
 * Small wrapper around `useQuery` so that we can use it imperatively. This is only needed becuase
 * `useLazyQuery` does not return a promise like the `refetch` method does.
 *
 * @see Credit: https://github.com/apollographql/react-apollo/issues/3499#issuecomment-586039082
 *
 * @example
 * const callQuery = useImperativeQuery(query)
 * const handleClick = async () => {
 *   const{ data, error } = await callQuery()
 * }
 */
export default function useImperativeQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  query: DocumentNode,
  options: QueryHookOptions<TData, TVariables> = {}
): QueryResult<TData, TVariables>['refetch'] {
  const { refetch } = useQuery<TData, TVariables>(query, {
    ...options,
    skip: true,
  });

  const imperativelyCallQuery = useCallback(
    (queryVariables?: Partial<TVariables>) => {
      return refetch(queryVariables);
    },
    [refetch]
  );

  return imperativelyCallQuery;
}
